import React, { useState } from "react";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import useChatAPI from "../hooks/useChatAPI";

const FeedbackComponent = ({ messageId, sessionId, initialFeedback = null }) => {
  const { submitFeedback } = useChatAPI();
  const [liked, setLiked] = useState(initialFeedback?.type === 'positive' ? true : initialFeedback?.type === 'negative' ? false : null);
  const [feedback, setFeedback] = useState(initialFeedback?.comment || "");
  const [isSubmitted, setIsSubmitted] = useState(!!initialFeedback);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await submitFeedback(
        sessionId, 
        messageId, 
        liked ? "positive" : "negative", 
        feedback
      );
      setIsSubmitted(true);
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to submit feedback:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLikeClick = (value) => {
    setLiked(value);
  };

  const handleEdit = () => {
    setIsEditing(true);
    setIsSubmitted(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setIsSubmitted(true);
    setLiked(initialFeedback?.type === 'positive' ? true : initialFeedback?.type === 'negative' ? false : null);
    setFeedback(initialFeedback?.comment || "");
  };

  if (isSubmitted && !isEditing) {
    return (
      <div className="mt-3 pt-3 border-t border-gray-200">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs text-gray-600">Your feedback:</span>
          {liked ? (
            <ThumbsUp size={14} className="text-black" />
          ) : (
            <ThumbsDown size={14} className="text-black" />
          )}
        </div>
        {feedback && (
          <div className="p-2 bg-gray-50 border border-gray-200 text-xs text-gray-600 text-left">
            {feedback}
          </div>
        )}
        <button
          className="text-xs text-gray-500 hover:text-black mt-2"
          onClick={handleEdit}
          type="button"
        >
          Edit feedback
        </button>
      </div>
    );
  }

  return (
    <div className="mt-3 pt-3 border-t border-gray-200">
      <div className="flex items-center gap-3 mb-2">
        <span className="text-xs text-gray-600">Was this helpful?</span>
        <div className="flex items-center gap-1">
          <button
            className={`p-1.5 border border-gray-300 hover:bg-gray-50 transition-colors ${
              liked === true ? "bg-gray-100 border-gray-400" : "bg-white"
            }`}
            onClick={() => handleLikeClick(true)}
            type="button"
            title="Helpful"
          >
            <ThumbsUp size={14} className={liked === true ? "text-black" : "text-gray-500"} />
          </button>
          <button
            className={`p-1.5 border border-gray-300 hover:bg-gray-50 transition-colors ${
              liked === false ? "bg-gray-100 border-gray-400" : "bg-white"
            }`}
            onClick={() => handleLikeClick(false)}
            type="button"
            title="Not helpful"
          >
            <ThumbsDown size={14} className={liked === false ? "text-black" : "text-gray-500"} />
          </button>
        </div>
      </div>

      {liked !== null && (
        <div className="space-y-2">
          <textarea
            className="w-full border border-gray-300 px-3 py-2 text-xs resize-none focus:outline-none focus:ring-1 focus:ring-black"
            rows={3}
            placeholder={
              liked === true
                ? "Tell us what went well..."
                : "Tell us what could be improved..."
            }
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
          />
          <div className="flex gap-2 justify-end">
            {isEditing && (
              <button
                className="text-xs px-3 py-1 text-gray-600 hover:text-black"
                onClick={handleCancel}
                type="button"
              >
                Cancel
              </button>
            )}
            {!isEditing && (
              <button
                className="text-xs px-3 py-1 text-gray-600 hover:text-black"
                onClick={() => {
                  setLiked(null);
                  setFeedback("");
                }}
                type="button"
              >
                Cancel
              </button>
            )}
            <button
              className="text-xs bg-electric-blue text-white px-3 py-1 hover:bg-electric-blue-eb3 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleSubmit}
              disabled={isSubmitting}
              type="button"
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackComponent;
